import { useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

const COLLAPSED_KEY = "loto.sidebar.collapsed";

export type Sidebar = {
  /** Escritorio: la barra se reduce a solo iconos. Persiste entre sesiones. */
  collapsed: boolean;
  toggleCollapsed: () => void;
  /** Móvil: el drawer está desplegado. Efímero, se cierra al navegar. */
  open: boolean;
  setOpen: (open: boolean) => void;
};

function readCollapsed(): boolean {
  // localStorage falla en modo privado de algunos navegadores; el estado por
  // defecto (expandida) es una degradación aceptable.
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * useSidebar gobierna los dos estados de la navegación lateral, que son independientes:
 * `collapsed` es una preferencia de escritorio (persistida), `open` es el drawer de móvil
 * (efímero). El drawer se cierra al cambiar de ruta y con Escape, porque tapa el contenido.
 */
export function useSidebar(): Sidebar {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSED_KEY, String(next));
      } catch {
        // Preferencia no persistida: no es motivo para romper la navegación.
      }
      return next;
    });
  }, []);

  // Navegar cierra el drawer: en móvil ocupa la pantalla y taparía la página destino.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `pathname` no se lee en el cuerpo, es la señal que dispara el cierre en cada navegación.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return { collapsed, toggleCollapsed, open, setOpen };
}
